// Business-facing entry point for completing Plaid micro-deposit verification. The partner mints
// a link (POST /v2/accounts/{accountId}/verify/link, Melio-Entity-Id header) and hands the URL to the
// business; opening it lands here. The scoped token carries the entity id, the account id, and the
// embedded Plaid update-mode link_token — so the flow reopens Plaid in update mode with no api-key in
// the browser. Completing update mode confirms the deposit with Plaid; the backend flips isVerified on
// the next account read, so there is no explicit verify submit.
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { config } from '@/config';
import { defaultEnvironment } from '@/config/environments';
import { decodeInternalAccountLinkToken, isTokenExpired } from '@/lib/sessionToken';
import { ensureSonarSession } from '@/lib/sonarSdk';

import { VerifyPlaidAccount } from '@/containers/VerifyPlaidAccount';
import { AccountShell, AccountError } from '@/containers/AccountShell';

export default function VerifyAccountRoute() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const env = useMemo(() => defaultEnvironment(config.environment, window.location.hostname), []);
  const claims = useMemo(() => (token ? decodeInternalAccountLinkToken(token) : null), [token]);

  useEffect(() => {
    if (!claims) return;
    void ensureSonarSession(env, claims.partnerName, claims.entityId);
  }, [env, claims]);

  if (!token || !claims) {
    return (
      <AccountShell>
        <AccountError title="This link isn’t valid" message="The link is missing or malformed. Ask your provider to send you a new one." />
      </AccountShell>
    );
  }

  if (isTokenExpired(claims)) {
    return (
      <AccountShell>
        <AccountError
          title="This link has expired"
          message="For your security the link is only valid for a short time. Ask your provider to send you a new one."
        />
      </AccountShell>
    );
  }

  if (claims.fundingSourceType !== 'plaid' || !claims.accountId) {
    return (
      <AccountShell>
        <AccountError
          title="This link isn’t valid"
          message="This link can’t be used to verify an account. Ask your provider to send you a new one."
        />
      </AccountShell>
    );
  }

  // No AccountShell here — Plaid's own overlay is the only UI most of the time, and the
  // sized/padded shell box would otherwise sit empty in the DOM behind it.
  return <VerifyPlaidAccount env={env} token={token} claims={claims} />;
}
